// src/components/common/Footer.js
import {
  ArrowDownward,
  ArrowUpward, // Añadir esta línea
  Article as BlogIcon,
  Chat as ChatIcon,
  ContactSupport as ContactIcon,
  Email as EmailIcon,
  Facebook,
  Forum as ForumIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Instagram,
  LibraryBooks as LibraryBooksIcon,
  PersonAdd as RegisterIcon,
  MedicalServices as SpecialistsIcon,
  Twitter,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles"; // Asegúrate de importar alpha
import React, { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import "../../styles/components/Footer.css";

// Componente para iconos de redes sociales memoizado para prevenir re-renders innecesarios
const SocialIcon = memo(({ network, icon, color }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Eliminamos la línea "const theme = useTheme();" ya que no se usa

  return (
    <Tooltip title={network} arrow>
      <IconButton
        href={`https://${network.toLowerCase()}.com`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("footer.socialMedia", { network })}
        sx={{
          color: theme.palette.common.white, // Después
          backgroundColor: alpha(theme.palette.common.white, 0.1), // Después
          transition: "all 0.3s",
          "&:hover": {
            backgroundColor: color,
            transform: "scale(1.15)",
          },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
});
SocialIcon.displayName = "SocialIcon";

const Footer = ({ mode }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Nuevo estado para el footer expandible en móviles
  const [expandedFooter, setExpandedFooter] = React.useState(false);

  // FIX: Solución para el error de aria-hidden
  React.useEffect(() => {
    // Buscar el elemento raíz que tiene aria-hidden="true" incorrectamente
    const rootElement = document.getElementById("root");
    if (rootElement && rootElement.getAttribute("aria-hidden") === "true") {
      // Eliminar el aria-hidden del contenedor raíz
      rootElement.removeAttribute("aria-hidden");
      console.log(
        "Corrección de accesibilidad: Se eliminó aria-hidden del elemento raíz",
      );
    }
  }, []);

  // Nueva función para controlar la expansión
  const toggleFooterContent = useCallback(() => {
    setExpandedFooter((prev) => !prev);
  }, []);

  // Memoizar la función para evitar recreaciones en re-renders
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Anunciar a lectores de pantalla
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", "polite");
    announcement.setAttribute("class", "sr-only");
    announcement.textContent = t("footer.scrolledToTop");
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }, [t]);

  // Manejar errores de carga del logo - memoizado para prevenir recreaciones
  const handleLogoError = useCallback(
    (e) => {
      e.target.src = "/assets/fallback-logo.png";
      e.target.onerror = () => {
        e.target.style.display = "none";
        // Crear texto alternativo cuando falla la imagen
        const parent = e.target.parentNode;
        if (parent && !parent.querySelector(".logo-fallback-text")) {
          const textNode = document.createElement("span");
          textNode.className = "logo-fallback-text";
          textNode.textContent = "VicDown";
          textNode.style.fontSize = "1.5rem";
          textNode.style.fontWeight = "bold";
          textNode.style.color = theme.palette.common.white;
          parent.appendChild(textNode);
        }
      };
    },
    [theme.palette.common.white],
  ); // <-- Añadir theme.palette.common.white aquí

  const navLinks = [
    { key: "home", icon: <HomeIcon />, path: "/" },
    { key: "blog", icon: <BlogIcon />, path: "/blog" },
    { key: "forum", icon: <ForumIcon />, path: "/forum" },
    { key: "specialists", icon: <SpecialistsIcon />, path: "/specialists" },
    { key: "contact", icon: <ContactIcon />, path: "/contact" },
    // Nuevos enlaces
    { key: "resources", icon: <LibraryBooksIcon />, path: "/resources-art" }, // Recursos
    { key: "chatbot", icon: <ChatIcon />, path: "/chatbot" }, // Chatbot
    { key: "information", icon: <InfoIcon />, path: "/info" }, // Información
  ];

  const policyLinks = [
    { key: "privacy", path: "/privacy" },
    { key: "cookies", path: "/cookies" },
    { key: "terms", path: "/terms" },
    { key: "subscriptions", path: "/subscriptions" },
    // --- Añadir estos nuevos enlaces ---
    { key: "community", path: "/community-guidelines" },
    { key: "disclaimer", path: "/disclaimer" },
    { key: "accessibility", path: "/accessibility" },
    // --- Fin de los nuevos enlaces ---
  ];

  const socialNetworks = [
    { name: "Facebook", icon: <Facebook />, color: "#4267B2" },
    { name: "Twitter", icon: <Twitter />, color: "#1DA1F2" },
    { name: "Instagram", icon: <Instagram />, color: "#C13584" },
  ];

  return (
    <Box
      component="footer"
      className="footer"
      sx={{
        backgroundColor:
          mode === "dark"
            ? "rgba(18, 25, 38, 0.97)" // Mismo color oscuro que la cabecera
            : theme.palette.primary.dark, // Color actual para modo claro
        color: theme.palette.common.white,
        pt: 6,
        pb: 4,
        boxShadow:
          mode === "dark"
            ? "0 -4px 10px rgba(0,0,0,0.25)" // Sombra más pronunciada para modo oscuro
            : "0 -4px 10px rgba(0,0,0,0.1)",
        position: "relative",
        transition:
          "background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
      }}
      role="contentinfo"
      aria-label={t("footer.ariaLabel") || "Pie de página"}
    >
      {/* Skip link para volver al contenido principal - mejora de accesibilidad */}
      <a
        href="#main-content"
        className="skip-link sr-only sr-only-focusable"
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          padding: "8px 12px",
          background: "#fff",
          color: theme.palette.primary.main,
          zIndex: 100,
          borderRadius: "4px",
        }}
      >
        {t("common.skipToContent") || "Volver al contenido principal"}
      </a>

      <Container maxWidth="lg">
        {/* Versión móvil simplificada */}
        <Box sx={{ display: { xs: "block", sm: "none" } }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            {/* Logo centrado */}
            <Box
              component="img"
              src="/assets/logo.png"
              alt={t("footer.logoAlt") || "Logo de VicDown"}
              onError={handleLogoError}
              sx={{
                width: 80,
                borderRadius: "8px",
                mb: 2,
              }}
            />

            {/* Enlaces principales - solo 4 más importantes */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
                mb: 3,
              }}
            >
              {navLinks.slice(0, 4).map((item) => (
                <IconButton
                  key={item.key}
                  component={RouterLink}
                  to={item.path}
                  aria-label={t(`footer.links.${item.key}`) || item.key}
                  sx={{
                    color: theme.palette.common.white,
                    backgroundColor: alpha(theme.palette.common.white, 0.1),
                    p: 1.5,
                  }}
                >
                  {item.icon}
                </IconButton>
              ))}
            </Box>

            {/* Botón para expandir/contraer */}
            <Button
              onClick={toggleFooterContent}
              endIcon={expandedFooter ? <ArrowUpward /> : <ArrowDownward />}
              sx={{
                color: theme.palette.common.white,
                mb: 2,
                textTransform: "none",
              }}
            >
              {expandedFooter
                ? t("footer.showLess") || "Ver menos"
                : t("footer.showMore") || "Ver más opciones"}
            </Button>

            {/* Contenido expandible */}
            {expandedFooter && (
              <Box sx={{ mt: 3 }}>
                {/* Enlaces adicionales */}
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    mb: 2,
                    fontSize: "1rem",
                    color: theme.palette.secondary.light,
                  }}
                >
                  {t("footer.allLinks") || "Todos los enlaces"}
                </Typography>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {navLinks.slice(4).map((item) => (
                    <Button
                      key={item.key}
                      component={RouterLink}
                      to={item.path}
                      startIcon={item.icon}
                      sx={{
                        color: theme.palette.common.white,
                        textTransform: "none",
                        fontSize: "1rem",
                        padding: "8px",
                      }}
                    >
                      {t(`footer.links.${item.key}`) || item.key}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            {/* Copyright siempre visible */}
            <Typography
              variant="caption"
              color={alpha(theme.palette.common.white, 0.6)}
              sx={{ mt: 2, display: "block" }}
            >
              © {new Date().getFullYear()} VicDown.{" "}
              {t("footer.rights") || "Todos los derechos reservados"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color={alpha(theme.palette.common.white, 0.45)}
                sx={{ fontSize: "0.65rem" }}
              >
                {t("footer.developedBy", "Desarrollado por")}
              </Typography>
              <a
                href="https://apisdom.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Apisdom - Desarrollo de software a medida, APIs e Inteligencia Artificial"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <img
                  src="/assets/apisdom-logo.webp"
                  alt="Apisdom - Desarrollo de software a medida, APIs e Inteligencia Artificial"
                  style={{
                    height: "14px",
                    width: "auto",
                    opacity: 0.55,
                    transition: "opacity 0.2s",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseOut={(e) => (e.currentTarget.style.opacity = "0.55")}
                />
              </a>
            </Box>
          </Box>
        </Box>

        {/* Versión escritorio (actual) */}
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          {/* Contenido actual completo */}
          <Grid
            container
            spacing={{ xs: 5, md: 0 }} // Cambiado de spacing={{ xs: 5, md: 3 }} a 0
            justifyContent="space-between" // Cambiado de center a space-between
            maxWidth="1100px"
            mx="auto"
          >
            {/* Columna 1: Logo y eslogan */}
            <Grid
              item
              xs={12}
              sm={6}
              md={3.5} // Reducido de 4 a 3.5
              sx={{
                textAlign: { xs: "center", sm: "left" },
                mb: { xs: 4, sm: 0 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "center", sm: "flex-start" },
                }}
              >
                {/* Logo con animación mejorada */}
                <Box
                  component="img"
                  src="/assets/logo.png"
                  alt={t("footer.logoAlt") || "Logo de VicDown"}
                  onError={handleLogoError}
                  sx={{
                    width: { xs: 100, sm: 120 },
                    borderRadius: "12px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    mb: 2.5,
                    transition: "all 0.4s ease",
                    "&:hover": {
                      transform: "scale(1.05) rotate(2deg)",
                      boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
                    },
                  }}
                />

                {/* Eslogan destacado con valor emocional y efecto hover */}
                {/* component="p": no es un heading semántico, solo subtítulo visual */}
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    fontFamily: "'Quicksand', sans-serif",
                    fontWeight: 600,
                    fontSize: { xs: "1.1rem", sm: "1.3rem" },
                    color: theme.palette.secondary.main, // Cambiado de light a main
                    mb: 1.5,
                    letterSpacing: "0.02em",
                    maxWidth: "280px",
                    position: "relative",
                    display: "inline-block",
                    paddingBottom: "2px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      "&:after": {
                        width: "100%",
                        opacity: 1,
                      },
                    },
                    "&:after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "0%",
                      height: "2px",
                      backgroundColor: `linear-gradient(45deg, #e0c33a 0%, #f7d64a 100%)`,
                      background: `linear-gradient(45deg, #e0c33a 0%, #f7d64a 100%)`,
                      opacity: 0,
                      transition: "width 0.4s ease, opacity 0.4s ease",
                    },
                  }}
                >
                  {t("footer.tagline") || "Apoyo para familias Down"}
                </Typography>

                {/* Descripción que aporta valor con efecto hover */}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "1rem", sm: "0.95rem" },
                    color: alpha(theme.palette.common.white, 0.85), // Después (o theme.palette.text.secondary si aplica)
                    maxWidth: "280px",
                    lineHeight: 1.6,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      color: "rgba(255,255,255,1)",
                      transform: "translateX(3px)",
                    },
                  }}
                >
                  {t("footer.mission") ||
                    "Conectando familias, compartiendo experiencias y recursos para una vida plena."}
                </Typography>
              </Box>
            </Grid>

            {/* Columna 2: Primera columna de enlaces (4 primeros) */}
            <Grid
              item
              xs={12}
              sm={6}
              md={2}
              sx={{
                textAlign: { xs: "center", sm: "left" },
                pl: { md: 4 }, // Padding-left de 2cm (32px) en pantallas md y mayores
                ml: { md: 2 }, // Margin-left adicional
              }}
            >
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  mb: 2.5,
                  fontWeight: 600,
                  color: theme.palette.secondary.main, // Cambiado de light a main
                  fontSize: "1.2rem",
                }}
              >
                {t("footer.quickLinks") || "Enlaces rápidos"}
              </Typography>

              <Box
                component="nav"
                aria-label={t("footer.navigation") || "Navegación principal"}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {navLinks.slice(0, 4).map((item) => (
                  <Button
                    key={item.key}
                    component={RouterLink}
                    to={item.path}
                    startIcon={item.icon}
                    sx={{
                      justifyContent: { xs: "center", sm: "flex-start" },
                      color: theme.palette.common.white,
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: { xs: "1.1rem", sm: "1rem" },
                      padding: { xs: "10px 15px", sm: "6px 12px" },
                      borderRadius: "8px",
                      width: { xs: "100%", sm: "fit-content" },
                      marginBottom: { xs: "10px", sm: "0" },
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.common.white, 0.1), // Después
                        transform: "translateX(5px)",
                        color: theme.palette.secondary.light,
                      },
                    }}
                    aria-label={t(`footer.links.${item.key}`) || item.key}
                  >
                    {t(`footer.links.${item.key}`) || item.key}
                  </Button>
                ))}
              </Box>
            </Grid>

            {/* Columna 3: Segunda columna de enlaces (4 últimos) */}
            <Grid
              item
              xs={12}
              sm={6}
              md={2}
              sx={{
                textAlign: { xs: "center", sm: "left" },
                pl: { md: 4 }, // Padding-left de 2cm (32px) en pantallas md y mayores
                ml: { md: 2 }, // Margin-left adicional
              }}
            >
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  mb: 2.5,
                  fontWeight: 600,
                  color: theme.palette.secondary.main, // Cambiado de light a main
                  fontSize: "1.2rem",
                }}
              >
                {"Enlaces adicionales"}
              </Typography>

              <Box
                component="nav"
                aria-label={
                  t("footer.additionalNavigation") || "Navegación adicional"
                }
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {navLinks.slice(4).map((item) => (
                  <Button
                    key={item.key}
                    component={RouterLink}
                    to={item.path}
                    startIcon={item.icon}
                    sx={{
                      justifyContent: { xs: "center", sm: "flex-start" },
                      color: theme.palette.common.white,
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: { xs: "1.1rem", sm: "1rem" },
                      padding: { xs: "10px 15px", sm: "6px 12px" },
                      borderRadius: "8px",
                      width: { xs: "100%", sm: "fit-content" },
                      marginBottom: { xs: "10px", sm: "0" },
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.common.white, 0.1), // Después
                        transform: "translateX(5px)",
                        color: theme.palette.secondary.light,
                      },
                    }}
                    aria-label={t(`footer.links.${item.key}`) || item.key}
                  >
                    {t(`footer.links.${item.key}`) || item.key}
                  </Button>
                ))}
              </Box>
            </Grid>

            {/* Columna 4: Contacto y redes sociales */}
            <Grid
              item
              xs={12}
              sm={6}
              md={3.5} // Reducido de 4 a 3.5 para mantener el balance
              sx={{
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  mb: 2.5,
                  fontWeight: 600,
                  color: theme.palette.secondary.main, // Cambiado de light a main
                  fontSize: "1.2rem",
                }}
              >
                {t("footer.connectWithUs") || "Conéctate con nosotros"}
              </Typography>

              {/* Correo electrónico destacado */}
              <Button
                href="mailto:info@VicDown.com"
                startIcon={<EmailIcon />}
                sx={{
                  justifyContent: { xs: "center", sm: "flex-start" },
                  mb: 3,
                  color: theme.palette.common.white,
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "1rem",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  width: "fit-content",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.common.white, 0.1), // Después
                    color: theme.palette.secondary.light,
                  },
                }}
                aria-label={
                  t("footer.contactEmail") || "Enviar email a info@VicDown.com"
                }
              >
                info@VicDown.com
              </Button>

              {/* Redes sociales con tooltips y componente memoizado */}
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  color: alpha(theme.palette.common.white, 0.85), // Después
                }}
              >
                {t("footer.followUs") || "Síguenos"}:
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  justifyContent: { xs: "center", sm: "flex-start" },
                }}
              >
                {socialNetworks.map((network) => (
                  <SocialIcon
                    key={network.name}
                    network={network.name}
                    icon={network.icon}
                    color={network.color}
                  />
                ))}
              </Box>

              {/* Botón de registro destacado - NUEVO */}
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "center", sm: "flex-start" },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.5,
                    color: alpha(theme.palette.common.white, 0.9), // Después
                    fontWeight: 500,
                  }}
                >
                  {t("footer.joinUs") || "Únete a nosotros"}:
                </Typography>

                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  startIcon={<RegisterIcon />}
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.common.white,
                    borderRadius: "25px",
                    padding: "10px 20px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: theme.palette.secondary.dark,
                      transform: "translateY(-3px)",
                      boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
                    },
                    "&:active": {
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {t("footer.registerNow") || "Regístrate ahora"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Divisor con color más suave - solo escritorio */}
        <Divider
          sx={{
            my: 4,
            display: { xs: "none", sm: "block" },
            borderColor:
              mode === "dark"
                ? alpha(theme.palette.common.white, 0.15)
                : alpha(theme.palette.common.white, 0.15),
          }}
        />

        {/* Políticas y derechos - más pequeños y organizados - solo escritorio */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", sm: "flex-end" },
            gap: 2,
          }}
        >
          {/* Enlaces a políticas */}
          <Box
            component="nav"
            aria-label={t("footer.policies.title") || "Políticas y términos"}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: { xs: 2, sm: 3 },
              mb: { xs: 2, sm: 0 },
            }}
          >
            {policyLinks.map((item) => (
              <Link
                key={item.key}
                component={RouterLink}
                to={item.path}
                underline="hover"
                sx={{
                  color: alpha(theme.palette.common.white, 0.7), // Después
                  fontSize: "0.85rem",
                  fontWeight: 400,
                  transition: "color 0.2s",
                  "&:hover": {
                    color: theme.palette.secondary.light,
                  },
                }}
                aria-label={t(`footer.policies.${item.key}`) || item.key}
              >
                {t(`footer.policies.${item.key}`) || item.key}
              </Link>
            ))}
          </Box>

          {/* Copyright */}
          <Typography
            variant="caption"
            color={alpha(theme.palette.common.white, 0.6)}
            sx={{ fontSize: "0.8rem" }}
          >
            © {new Date().getFullYear()} VicDown.{" "}
            {t("footer.rights") || "Todos los derechos reservados"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Typography
              variant="caption"
              color={alpha(theme.palette.common.white, 0.45)}
              sx={{ fontSize: "0.7rem" }}
            >
              {t("footer.developedBy", "Desarrollado por")}
            </Typography>
            <a
              href="https://apisdom.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Apisdom - Desarrollo de software a medida, APIs e Inteligencia Artificial"
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <img
                src="/assets/apisdom-logo.webp"
                alt="Apisdom - Desarrollo de software a medida, APIs e Inteligencia Artificial"
                style={{
                  height: "14px",
                  width: "auto",
                  opacity: 0.55,
                  transition: "opacity 0.2s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.opacity = "0.9")}
                onMouseOut={(e) => (e.currentTarget.style.opacity = "0.55")}
              />
            </a>
          </Box>
        </Box>

        {/* Botón de subir con mejor posición y estilo */}
        <Tooltip
          title={t("footer.scrollToTop") || "Volver arriba"}
          arrow
          placement="top"
        >
          <IconButton
            onClick={scrollToTop}
            aria-label={
              t("footer.scrollToTop") || "Volver al inicio de la página"
            }
            tabIndex={0} // Asegurar que sea enfocable con el teclado
            aria-live="polite" // Anunciar cambios a lectores de pantalla
            sx={{
              position: "absolute",
              bottom: { xs: 80, sm: 100 }, // Posicionado más arriba
              right: "50%", // Centrado horizontalmente
              transform: "translateX(50%)", // Ajuste para centrado perfecto
              color: theme.palette.common.white, // Después
              backgroundColor:
                mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.8)
                  : theme.palette.primary.main,
              boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
              width: { xs: 45, sm: 52 }, // Tamaño responsive
              height: { xs: 45, sm: 52 },
              "&:hover": {
                backgroundColor: theme.palette.secondary.main,
                transform: "translateX(50%) translateY(-8px)",
                boxShadow: "0 8px 25px rgba(247, 214, 74, 0.5)",
              },
              "&:before": {
                // Efecto de halo alrededor
                content: '""',
                position: "absolute",
                top: -4,
                left: -4,
                right: -4,
                bottom: -4,
                borderRadius: "50%",
                background: "linear-gradient(45deg, #e0c33a, transparent)",
                opacity: 0,
                transition: "opacity 0.3s",
                zIndex: -1,
              },
              "&:hover:before": {
                opacity: 0.8,
                animation: "pulse 1.5s infinite",
              },
              "@keyframes pulse": {
                "0%": { transform: "scale(1)", opacity: 0.7 },
                "50%": { transform: "scale(1.1)", opacity: 0.9 },
                "100%": { transform: "scale(1)", opacity: 0.7 },
              },
              transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              zIndex: 10, // Asegurar que esté por encima de otros elementos
              padding: 0,
              // Añadir indicador de foco visible para usuarios de teclado
              "&:focus-visible": {
                outline: `3px solid ${theme.palette.secondary.main}`,
                outlineOffset: 3,
              },
            }}
          >
            <ArrowUpward
              sx={{
                fontSize: { xs: "1.5rem", sm: "1.8rem" },
                transition: "all 0.3s",
                animation: "bounce 2s infinite",
                "@keyframes bounce": {
                  "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
                  "40%": { transform: "translateY(-6px)" },
                  "60%": { transform: "translateY(-3px)" },
                },
              }}
            />
          </IconButton>
        </Tooltip>
      </Container>
    </Box>
  );
};

// Exportar componente memoizado para prevenir re-renders innecesarios
export default memo(Footer);
